<script lang="ts" setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    text: string;
    scrollTarget: Element;
    delay?: number;
    noTransition?: boolean;
  }>(),
  {
    delay: 500,
    noTransition: false,
  },
);

const showTooltip = ref(false);
const placedAbove = ref(false);
const left = ref(0);
const top = ref(0);
const tooltipEl = ref<HTMLElement | null>(null);
let hideTimerId: ReturnType<typeof setTimeout> | null = null;
const ANCHOR_OFFSET = 6;

function clearHideTimer() {
  if (hideTimerId !== null) {
    clearTimeout(hideTimerId);
    hideTimerId = null;
  }
}

function show() {
  clearHideTimer();
  showTooltip.value = true;
}

function hide() {
  clearHideTimer();
  hideTimerId = setTimeout(() => {
    showTooltip.value = false;
    hideTimerId = null;
  }, props.delay);
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === "Escape" && showTooltip.value) {
    showTooltip.value = false;
  }
}

function reposition() {
  if (!props.scrollTarget || !tooltipEl.value) return;
  const anchorRect = props.scrollTarget.getBoundingClientRect();
  const tipRect = tooltipEl.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const spaceAbove = anchorRect.top;
  const tipNeeded = tipRect.height + ANCHOR_OFFSET + 8;

  placedAbove.value = spaceBelow < tipNeeded && spaceAbove > spaceBelow;
  left.value = anchorRect.left + anchorRect.width / 2 - tipRect.width / 2;
  top.value = placedAbove.value
    ? anchorRect.top - tipRect.height - ANCHOR_OFFSET
    : anchorRect.bottom + ANCHOR_OFFSET;
}

function addListeners(el: Element) {
  el.addEventListener("mouseover", show);
  el.addEventListener("mouseleave", hide);
  el.addEventListener("focus", show);
  el.addEventListener("blur", hide);
}

function removeListeners(el: Element) {
  el.removeEventListener("mouseover", show);
  el.removeEventListener("mouseleave", hide);
  el.removeEventListener("focus", show);
  el.removeEventListener("blur", hide);
}

onMounted(() => {
  if (props.scrollTarget) addListeners(props.scrollTarget);
  window.addEventListener("keydown", handleKeydown);
});

onBeforeUnmount(() => {
  if (props.scrollTarget) removeListeners(props.scrollTarget);
  window.removeEventListener("keydown", handleKeydown);
  clearHideTimer();
});

watch(
  () => props.scrollTarget,
  (newEl, oldEl) => {
    if (oldEl) removeListeners(oldEl);
    if (newEl) addListeners(newEl);
  },
);

watch(showTooltip, async (v) => {
  if (v) {
    await nextTick();
    reposition();
  }
});
</script>

<template>
  <Teleport to="body">
    <Transition :name="noTransition ? 'none' : 'm-tooltip-fade'">
      <div
        v-if="showTooltip"
        ref="tooltipEl"
        class="m-tooltip"
        :class="placedAbove ? 'm-tooltip--above' : 'm-tooltip--below'"
        :style="{ left: `${left}px`, top: `${top}px` }"
        role="tooltip"
        @mouseover="show"
        @mouseleave="hide"
      >
        <div class="m-tooltip__arrow" />
        <div class="m-tooltip__text">{{ text }}</div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss">
.m-tooltip {
  position: fixed;
  z-index: 9999;
  pointer-events: all;
  max-width: 250px;
  padding: var(--space-1) var(--space-2);
  font-size: var(--font-size-xs);
  line-height: 1.4;
  background: var(--theme-grey);
  color: var(--text-on-dark);
  border-radius: var(--radius-default);
  white-space: pre-line;

  &__arrow {
    position: absolute;
    width: 8px;
    height: 8px;
    background: inherit;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    border-radius: var(--radius-xs);
  }

  &--below &__arrow {
    top: -4px;
  }

  &--above &__arrow {
    bottom: -4px;
  }
}

.m-tooltip-fade-enter-active,
.m-tooltip-fade-leave-active {
  transition: opacity 150ms ease;
}

.m-tooltip-fade-enter-from,
.m-tooltip-fade-leave-to {
  opacity: 0;
}
</style>
